/**
 * V1 per-product document checklist — required, conditional, deferred.
 */

import { V1_PRODUCT_IDS } from './products.js';

const num = (v) => {
  const n = parseFloat(String(v || '').replace(/,/g, ''));
  return Number.isFinite(n) ? n : 0;
};

const defs = {
  govId: {
    id: 'gov-id',
    label: 'Government-issued photo ID',
    hint: 'Driver license, passport, or state ID — full image, not cropped.',
    required: true,
  },
  paystubs: {
    id: 'paystubs',
    label: 'Two most recent paystubs (within 30 days)',
    required: true,
  },
  w2TwoYears: {
    id: 'w2-two-years',
    label: 'W-2s for the last two years',
    required: true,
  },
  bankStatements: {
    id: 'bank-statements',
    label: 'Two months bank statements (all pages)',
    required: true,
  },
  assetStatement: {
    id: 'asset-statement',
    label: 'Most recent investment or asset statement',
    hint: 'Only if you are using these funds for closing or reserves.',
    required: true,
    showIf: (ctx) => num(ctx.fieldAnswers?.liquidAssets) > 0,
  },
  giftLetter: {
    id: 'gift-letter',
    label: 'Gift letter and donor documentation',
    required: true,
    showIf: (ctx) => ctx.fieldAnswers?.giftFunds === 'yes',
  },
  bankruptcyDischarge: {
    id: 'bankruptcy-discharge',
    label: 'Bankruptcy discharge papers',
    required: true,
    showIf: (ctx) => ctx.fieldAnswers?.bankruptcyLast2Years === 'yes',
  },
  creditExplanation: {
    id: 'credit-explanation',
    label: 'Brief letter of explanation for derogatory credit',
    required: false,
    showIf: (ctx) =>
      ctx.fieldAnswers?.bankruptcyLast2Years === 'yes'
      || ctx.fieldAnswers?.foreclosureLast3Years === 'yes',
  },
  mortgageStatement: {
    id: 'mortgage-statement',
    label: 'Most recent mortgage statement',
    required: true,
  },
  paymentHistory: {
    id: 'payment-history',
    label: '12-month mortgage payment history (or verification of mortgage)',
    required: true,
  },
  bankStatementsClosing: {
    id: 'bank-statements-closing',
    label: 'Two months bank statements (if needed for closing costs)',
    hint: 'Upload if you will bring cash to closing from these accounts.',
    required: false,
    showIf: (ctx) => num(ctx.fieldAnswers?.liquidAssets) > 0,
  },
  dd214: {
    id: 'dd214',
    label: 'DD-214 or proof of military service',
    required: true,
    showIf: (ctx) => ctx.fieldAnswers?.coeOnFile === 'no',
  },
  hoaStatement: {
    id: 'hoa-statement',
    label: 'HOA statement or budget',
    required: true,
    showIf: (ctx) => num(ctx.fieldAnswers?.hoaMonthly) > 0,
  },
  juniorLienStatement: {
    id: 'junior-lien-statement',
    label: 'Second lien / HELOC statement',
    required: true,
    showIf: (ctx) => ctx.fieldAnswers?.hasJuniorLien === 'yes',
  },
};

const pick = (...keys) => keys.map((k) => defs[k]).filter(Boolean);

export const V1_DEFERRED_TO_LENDER = [
  'Verification of employment (VOE)',
  'Tax returns (unless lender requests for your file type)',
  'Purchase contract (provide when under contract)',
  'Appraisal (ordered by lender)',
];

export const V1_PRODUCT_DOCUMENT_KEYS = {
  [V1_PRODUCT_IDS.CONVENTIONAL_PURCHASE]: [
    'govId', 'paystubs', 'w2TwoYears', 'bankStatements', 'assetStatement', 'giftLetter',
  ],
  [V1_PRODUCT_IDS.FHA_PURCHASE]: [
    'govId', 'paystubs', 'w2TwoYears', 'bankStatements', 'assetStatement', 'giftLetter',
    'bankruptcyDischarge', 'creditExplanation',
  ],
  [V1_PRODUCT_IDS.FHA_STREAMLINE]: [
    'mortgageStatement', 'paymentHistory', 'govId', 'bankStatementsClosing',
  ],
  [V1_PRODUCT_IDS.VA_IRRRL]: [
    'mortgageStatement', 'paymentHistory', 'govId', 'dd214',
  ],
  [V1_PRODUCT_IDS.CONVENTIONAL_RATE_TERM]: [
    'govId', 'paystubs', 'w2TwoYears', 'bankStatements', 'mortgageStatement',
    'assetStatement', 'hoaStatement', 'juniorLienStatement',
  ],
  [V1_PRODUCT_IDS.CONVENTIONAL_CASH_OUT]: [
    'govId', 'paystubs', 'w2TwoYears', 'bankStatements', 'mortgageStatement',
    'assetStatement', 'hoaStatement', 'juniorLienStatement',
  ],
};

export const buildV1DocumentContext = (triageAnswers = {}, fieldAnswers = {}) => ({
  triageAnswers,
  fieldAnswers,
});

export const getVisibleV1Documents = (productId, ctx) => {
  const keys = V1_PRODUCT_DOCUMENT_KEYS[productId] || [];
  return pick(...keys).filter((d) => !d.showIf || d.showIf(ctx));
};

export const getRequiredV1Documents = (productId, ctx) =>
  getVisibleV1Documents(productId, ctx).filter((d) => d.required !== false);

export const getV1DocCompletenessPercent = (productId, ctx, uploadedByDocId = {}) => {
  const required = getRequiredV1Documents(productId, ctx);
  if (required.length === 0) return 100;
  const done = required.filter((d) => uploadedByDocId[d.id]).length;
  return Math.round((done / required.length) * 100);
};
